import React, { useState, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';

import fundingApi from '/src/apis/fundingApi.js';
import investorApi from '/src/apis/investorApi.js';

import { ALLOWED_QUERY_OPERATOR, TAXONOMIES, ENTITY_TYPE } from '/src/lib/constants';
import { MAGNITT_ACCESS_ROLES } from '/src/lib/constants/userRoles';
import { amountFormatter } from '/src/lib/helpers';

import {
    renderLockedFundingTablesSection,
    renderLockedFundingRoundsHeader,
    renderLockedInvestorInvestmentsSection,
} from '/src/components/LockedContent';

import InvestmentsFilters from './InvestmentsFilters';
import InvestmentTable from './InvestmentTable';
import InvestmentSummaryCards from './Components/InvestmentSummaryCards/InvestmentSummaryCards';
import DeploymentTimeline from '../PortfolioInsights/DeploymentTimeline';
import PortfolioComposition from '../PortfolioInsights/PortfolioComposition';
import EstimationIndicator from '/src/components/Reusable/EstimationIndicator';
import InvestmentFundTabs from './Components/InvestmentFundTabs/InvestmentFundTabs';
import Pagination from '/src/components/Pagination/Pagination';
import AppMagnittLoader from '/src/components/Reusable/AppMagnittLoader';
import NewBadge from '/src/components/Reusable/NewBadge';

const INVESTED_IN_TYPES = [ENTITY_TYPE.startup, ENTITY_TYPE.corporate];
const RECORDS_TO_LOAD = 10;
const LIMIT_OPTIONS = [10, 20, 30];
const BASE_FILTERS = [

    { key: 'investedin_type_filter', value: INVESTED_IN_TYPES, op: ALLOWED_QUERY_OPERATOR.equal },
];

const InvestorInvestmentsSection = ({ entityData, entityType, entityId, isTeamMember, user, owner, setState, requestedFundId = null, onFundIdConsumed = null }) => {
    const hasDirectoryAccess = user?.roles?.includes(MAGNITT_ACCESS_ROLES.directory_access)
        || user?.roles?.includes(MAGNITT_ACCESS_ROLES.limited_directory_access);
    const showAllRounds = !!(isTeamMember || hasDirectoryAccess);

    // Data state
    // null  = not yet initiated
    // false = in-flight (show loader)
    // []    = loaded but empty
    const [rounds, setRounds] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(RECORDS_TO_LOAD);
    const [isLoading, setIsLoading] = useState(false);

    const [investmentStats, setInvestmentStats] = useState({});
    const [numberOfInvestments, setNumberOfInvestments] = useState(0);
    const [numberOfFundedStartups, setNumberOfFundedStartups] = useState(0);
    const [totalInvestmentAmount, setTotalInvestmentAmount] = useState('');

    // Date column defaults to descending on initial load
    const [sortConfig, setSortConfig] = useState({ key: 'date_raised', dir: 'desc' });

    const [investmentFundTabFilters, setInvestmentFundTabFilters] = useState([]);
    const [aggregationFundFilters, setAggregationFundFilters] = useState([]);
    const [directoryFundFilter, setDirectoryFundFilter] = useState(null);

    const [userSelectedFilters, setUserSelectedFilters] = useState([]);
    const [isShowFilter, setIsShowFilter] = useState(false);
    const [isFiltered, setIsFiltered] = useState(false);

    // Prevent double-fetch when isTeamMember resolves
    const hasFetched = useRef(false);
    // Discard responses from superseded requests
    const fetchRoundsId = useRef(0);

    // Initial fetch — fires once isTeamMember is resolved
    useEffect(() => {
        if (!user?.is_loggedin) return;
        if (isTeamMember === '') return;
        if (hasFetched.current) return;
        hasFetched.current = true;
        fetchRounds();
    }, [isTeamMember]);

    // Re-fetch aggregation when fund tab filter changes
    useEffect(() => {
        if (Object.keys(investmentStats).length === 0) return;
        fetchAggregation(aggregationFundFilters);
    }, [aggregationFundFilters]);

    // sortOverride: pass the new sortConfig directly to avoid stale-closure issues
    const fetchRounds = (selectedFilters = [], resetPage = false, sortOverride) => {
        if (!user?.is_loggedin) return;

        const pageNum = resetPage ? 1 : page;
        if (resetPage) setPage(1);

        const filterList = [
            ...BASE_FILTERS,
            { key: 'funding_stage_type_filter', value: [TAXONOMIES.exitsFundingStage], op: ALLOWED_QUERY_OPERATOR.notEq },
            // the deals the section header counts: an active funding or PE stage, a duplicated investor row once
            { key: 'direct_investments_only', value: [1], op: ALLOWED_QUERY_OPERATOR.equal },
            ...selectedFilters,
            ...(investmentFundTabFilters ?? []),
        ];

        const effectiveSort = sortOverride !== undefined ? sortOverride : sortConfig;

        const requestId = ++fetchRoundsId.current;
        setRounds(false);

        fundingApi.getFundingData(entityId, entityType, limit, filterList, null, pageNum, '', effectiveSort)
            .then(res => {
                if (requestId !== fetchRoundsId.current) return;
                setRounds(res.rounds || []);
                setTotalRecords(res.totalRecords || 0);
                if (resetPage) setPage(1);
                setIsLoading(false);

                if (Object.keys(investmentStats).length === 0) {
                    fetchAggregation([]);
                }
            })
            .catch(() => {
                if (requestId !== fetchRoundsId.current) return;
                setRounds([]);
                setIsLoading(false);
            });
    };

    const fetchAggregation = (fundFilters = []) => {
        const isFiltered = fundFilters.length > 0;
        investorApi.getInvestmentsAggregation(entityId, entityType, INVESTED_IN_TYPES, fundFilters)
            .then(response => {
                if (isEmpty(response)) return;

                const totalAmount = response.total_amount
                    ? amountFormatter(parseInt(response.total_amount, 10)) : '';
                const capitalDeployed = response.estimated_deployed_capital
                    ? amountFormatter(parseInt(response.estimated_deployed_capital, 10)) : '';

                if (!isFiltered) {
                    setNumberOfInvestments(response.number_of_investments || 0);
                    setNumberOfFundedStartups(response.number_of_entities || 0);
                    setTotalInvestmentAmount(totalAmount);
                }

                setInvestmentStats(prev => ({
                    // On filtered calls keep the existing count so InvestmentFundTabs
                    // does not re-run loadFundsData and reset the active tab.
                    numberOfInvestments: isFiltered
                        ? prev?.numberOfInvestments
                        : response.number_of_investments,
                    allStageInvestmentsCount: response.all_stage_investments_count,
                    allStageRoundsCount: response.all_stage_rounds_count,
                    followOnInvestmentsCount: response.follow_on_investments_count,
                    followOnRatePercentage: response.follow_on_rate_percentage,
                    medianCheckSize: response.median_check_size,
                    capitalDeployed,
                    pctDeployed: response.pct_deployed != null ? response.pct_deployed : null,
                    exitsFromFund: response.num_of_fund_exits,
                    // the share of the direct-investment companies (startups and corporates) still active
                    stillActivePercentage: response.percentage_of_active_companies,
                    totalPortfolioCompanies: response.number_of_entities,
                }));
            })
            .catch(() => {});
    };


    const handleSort = (apiKey) => {
        const newDir = sortConfig?.key === apiKey && sortConfig?.dir === 'asc' ? 'desc' : 'asc';
        const newSortConfig = { key: apiKey, dir: newDir };
        setSortConfig(newSortConfig);
        fetchRounds(userSelectedFilters, true, newSortConfig);
    };

    const handleShowFilter = () => {
        if (Array.isArray(rounds) && rounds.length === 0) {
            const clearBtn = document.getElementById('clear-filters-button');
            if (clearBtn) clearBtn.click();
            setTimeout(() => {
                setIsShowFilter(v => !v);
                setIsFiltered(v => !v);
            }, 100);
        } else if (Array.isArray(rounds) && rounds.length > 0) {
            setIsShowFilter(v => !v);
            setIsFiltered(v => !v);
        }
    };

    const handleUserSelectedFilters = (_mainFilters = [], selectedFilters = []) => {
        setUserSelectedFilters(selectedFilters);
        fetchRounds(selectedFilters, true);
    };

    // Shared state updater passed to InvestmentFundTabs / Pagination
    const updateState = (newState) => {
        if (newState.rounds !== undefined) setRounds(newState.rounds);
        if (newState.totalRecords !== undefined) setTotalRecords(newState.totalRecords);
        if (newState.page !== undefined) setPage(newState.page);
        if (newState.limit !== undefined) { setLimit(newState.limit); setPage(1); }
        if (newState.isLoading !== undefined) setIsLoading(newState.isLoading);
    };

    // Access control
    // Wait for team status to resolve before rendering
    if (user?.is_loggedin && isTeamMember === '') return null;

    if (!user?.is_loggedin) {
        return renderLockedFundingTablesSection(entityType, entityData?.name, 'investorInvestments', setState);
    }

    if (rounds === null) return null;

    const entityCount = (
        <><span className="section-subtitle__count">{numberOfFundedStartups}</span> {numberOfFundedStartups !== 1 ? 'Companies' : 'Company'}</>
    );
    const totalAndEntityCount = totalInvestmentAmount
        ? <>totalling <span className="section-subtitle__count">{totalInvestmentAmount}</span> across {entityCount}</>
        : <>across {entityCount}</>;

    const directoryParams = new URLSearchParams({
        investor_filter: String(entityId),
        investor_type_filter: String(entityType),
        viewtype: 'fundingview',
    });
    if (Array.isArray(directoryFundFilter) && directoryFundFilter.length > 0) {
        directoryParams.set('fund_filter', directoryFundFilter.join(','));
    }
    const fullDealDataUrl = `/startups?${directoryParams.toString()}`;

    const showFilters = showAllRounds && entityType !== 'startup';
    const filterIcon = showFilters ? (
        <button type="button" onClick={handleShowFilter} className="funding-filters-button">
            <i className="fa fa-filter" /> Filters
        </button>
    ) : null;

    const showPagination = showAllRounds && (totalRecords > LIMIT_OPTIONS[0]);

    // True when rounds is momentarily empty because a fund tab was just cleared
    // (e.g. switching away from a 0-investment fund back to "All funds").
    // In this state the component must stay mounted so InvestmentFundTabs can
    // trigger the next fetchRounds via its useEffect.
    // Distinct from the investor-has-no-investments case (numberOfInvestments === 0),
    // where the section should be hidden entirely.
    const isTransientEmptyWhileSwitchingTabs =
        Array.isArray(rounds) && !rounds.length
        && !isFiltered && !investmentFundTabFilters?.length
        && numberOfInvestments > 0;

    let body;
    if (!showAllRounds) {
        body = renderLockedInvestorInvestmentsSection();
    } else if (rounds === false) {
        body = <AppMagnittLoader />;
    } else if (!rounds.length) {
        if (isFiltered) {
            body = (
                <div className="col-sm-12 py-1">
                    <h6 className="d-flex justify-content-center m-4">
                        No results were found. Please change your filters and try again.
                    </h6>
                </div>
            );
        } else if (investmentFundTabFilters.length > 0) {
            body = (
                <div className="col-sm-12 py-1" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <h6 className="m-4">No investments to display for this fund.</h6>
                </div>
            );
        } else if (isTransientEmptyWhileSwitchingTabs) {
            body = null;
        } else {
            return null;
        }
    } else {
        body = (
            <InvestmentTable
                rounds={rounds}
                entityId={entityId}
                entityType={entityType}
                hasDirectoryAccess={hasDirectoryAccess}
                onSort={handleSort}
                sortConfig={sortConfig}
            />
        );
    }

    return (
        <div className="funding-box">
            <div id="section-investorinvestments" className="section-investorinvestments">
                {!showAllRounds && renderLockedFundingRoundsHeader(entityType, 'investorInvestments')}

                <div className="profile-section-box fundingrounds">
                    <div className="funding-title">
                        <div className="title">
                            <h2 className="profile-section-heading">
                                Direct Investments <NewBadge className="px-2" />
                            </h2>
                            <span className="section-subtitle">
                                <span className="section-subtitle__name">{entityData?.name}</span> has made{' '}
                                <span className="section-subtitle__count">{numberOfInvestments}</span>{' '}
                                investment{numberOfInvestments !== 1 ? 's' : ''}{' '}
                                {totalAndEntityCount}
                            </span>
                        </div>
                        <div className="funding-title__actions">
                            <a
                                href={fullDealDataUrl}
                                className="funding-filters-button funding-title__full-data-link"
                            >
                                View in deal directory{' '}
                                <i className="fa fa-chevron-right" aria-hidden="true" />
                            </a>
                        </div>
                    </div>

                    {showAllRounds && (
                        <>
                            <InvestmentFundTabs
                                hasDirectoryAccess={hasDirectoryAccess}
                                entityId={entityId}
                                entityType={entityType}
                                userSelectedFilters={userSelectedFilters}
                                setInvestmentFundTabFilters={setInvestmentFundTabFilters}
                                investmentFundTabFilters={investmentFundTabFilters}
                                onDirectoryFundFilterChange={setDirectoryFundFilter}
                                setAggregationFundFilters={setAggregationFundFilters}
                                getFunding={fetchRounds}
                                updateParentState={updateState}
                                investmentStats={investmentStats}
                                requestedFundId={requestedFundId}
                                onFundIdConsumed={onFundIdConsumed}
                            />
                            <InvestmentSummaryCards investmentStats={investmentStats} />

                            <div className="portfolio-insights">
                                <div className="pi-grid">
                                    <DeploymentTimeline
                                        entityId={entityId}
                                        entityType={entityType}
                                        fundFilters={aggregationFundFilters}
                                    />
                                    <PortfolioComposition
                                        entityId={entityId}
                                        entityType={entityType}
                                        fundFilters={aggregationFundFilters}
                                    />
                                </div>
                            </div>

                            <div className="investment-fund-tabs__actions--standalone">
                                <EstimationIndicator className="estimation-legend--standalone" />
                                {filterIcon && (
                                    <div className="investment-fund-tabs__actions">
                                        {filterIcon}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    <InvestmentsFilters
                        entityType={entityType}
                        isInvestment={true}
                        mainFilter={BASE_FILTERS}
                        isShowFilter={isShowFilter}
                        handleShowFilter={handleShowFilter}
                        getFunding={handleUserSelectedFilters}
                        updateParentState={updateState}
                        hideIndustryFilter={false}
                    />

                    {body}

                    {showPagination && (
                        <div className="funding-table-pagination" data-table-type="investorInvestments">
                            <Pagination
                                page={page}
                                totalRecords={totalRecords}
                                limit={limit}
                                limitOptions={LIMIT_OPTIONS}
                                setParentState={updateState}
                                fetchData={() => fetchRounds(userSelectedFilters)}
                                isLoading={isLoading}
                                tableName="section-investorinvestments"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InvestorInvestmentsSection;
